import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  NgZone,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  map,
  Observable,
  of,
  switchMap,
  tap,
  Subscription,
} from 'rxjs';
import { PERSON_TITLES } from '../../config/person-titles';
import { CountryHttpService } from '../../shared/services/country.service';
import { NgSelectModule } from '@ng-select/ng-select';
import { CountryModel } from '../../shared/models/countryModel';
import { MedicalAidService } from '../../shared/services/medical-aid.service';
import { MedicalAidSchemeModel } from '../../shared/models/medicalAidSchemeModel';
import { MedicalAidOptionModel } from '../../shared/models/medicalAidOptionModel';
import { MedicalAidPlanModel } from '../../shared/models/medicalAidPlanModel';
import { OnboardingServiceService } from '../../shared/services/onboarding.service';
import { ActivatedRoute, Router } from '@angular/router';
import { GoogleMapsModule } from '@angular/google-maps';
import { GoogleMapsLoaderService } from '../../shared/services/google-map-loader.service';
import { environment } from '../../../environments/environment';
import { createPhoneNumberValidator } from '../../shared/validators/phone-validator';

@Component({
  selector: 'app-onboard-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgSelectModule,
    GoogleMapsModule,
  ],
  providers: [CountryHttpService, MedicalAidService, OnboardingServiceService],
  templateUrl: './onboarding-form.component.html',
  styleUrls: ['./onboarding-form.component.scss'],
})
export class OnboardingFormComponent implements OnInit, OnDestroy {
  @ViewChild('streetInput') set streetInput(
    element: ElementRef<HTMLInputElement>
  ) {
    if (element) {
      this.initAutocomplete(element);
    }
  }

  form!: FormGroup;
  addressForm!: FormGroup;
  medicalAidForm!: FormGroup;
  showMedicalAidFields: boolean = false;
  practiceLogo = '';
  personTitles = PERSON_TITLES;
  paymentTypes = [
    { id: 1, name: 'Cash' },
    { id: 2, name: 'Medical Aid' },
    { id: 3, name: 'NHI/Government Subsidy' },
  ];
  schemes: MedicalAidSchemeModel[] = [];
  plans: MedicalAidPlanModel[] = [];
  options: MedicalAidOptionModel[] = [];
  $countries!: Observable<CountryModel[]>;
  loading: boolean = true;
  error: string | null = null;
  profileUpdateError: string | null = null;
  validationErrors: Record<string, string> = {};
  isSubmitting = false;

  private defaultCountryCode = 'ZA';
  private tokenClaims?: Record<string, any>;
  private token$ = new BehaviorSubject<string | null>(null);
  private subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private onboardingServiceService: OnboardingServiceService,
    private route: ActivatedRoute,
    private router: Router,
    private countryService: CountryHttpService,
    private medicalService: MedicalAidService,
    private ngZone: NgZone,
    private googleMapsLoaderService: GoogleMapsLoaderService
  ) {}

  get appSource() {
    if (this.tokenClaims && this.tokenClaims['iss']) {
      this.tokenClaims['iss'];

      const match = /^https?:\/\/api-?([a-z0-9]+)?\.dev2?\.epione\.net/gi.exec(
        this.tokenClaims['iss']
      );
      if (match && match[1]) {
        return match[1];
      }
    }
    return environment.app_version;
  }

  ngOnInit(): void {
    this.googleMapsLoaderService.loadApi(environment.google_api_key);
    this.initForms();
    this.setupFormSubscriptions();
    const token = this.route.snapshot.queryParamMap.get('token');
    if (token) {
      this.redeemToken(token);
    } else {
      this.error = 'No Authentication provided';
      this.loading = false;
      this.error = 'Invalid form token';
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  submit() {
    this.profileUpdateError = '';
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.updateValidationErrors();
      return;
    }

    this.isSubmitting = true;
    this.validationErrors = {};
    const sub = this.onboardingServiceService
      .updateProfile(this.form.value, {
        headers: {
          Authorization: `Bearer ${this.token$.value}`,
          'x-epione-source': this.appSource,
        },
      })
      .pipe(
        tap(() => this.router.navigate(['/thank-you'])),
        catchError((err) => {
          this.profileUpdateError = err.statusText || err.message;
          if (err.status === 422) {
            for (const key in err.error.errors) {
              if (err.error.errors.hasOwnProperty(key)) {
                const errorObject = err.error.errors[key];
                const firstErrorKey = Object.keys(errorObject)[0];
                if (firstErrorKey) {
                  const errorMessage = errorObject[firstErrorKey];

                  this.validationErrors[key] = errorMessage;
                  const control = this.form.get(key);
                  if (control) {
                    control.setErrors({ backend: errorMessage });
                    control.markAsTouched();
                  }
                }
              }
            }
            this.updateValidationErrors();
          }
          return of(null);
        }),
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe();
    this.subscriptions.push(sub);
  }

  isInvalid(controlName: string, form: FormGroup = this.form): boolean {
    const control = form.get(controlName);
    return !!(
      (control && control.invalid && (control.touched || control.dirty)) ||
      this.validationErrors[controlName]
    );
  }

  private initForms() {
    this.addressForm = this.fb.group({
      unit_number: [''],
      complex: [''],
      street: ['', Validators.required],
      post_code: ['', Validators.required],
      city: ['', Validators.required],
      municipality: ['', Validators.required],
      province: ['', Validators.required],
      country_id: ['', Validators.required],
    });

    this.medicalAidForm = this.fb.group({
      medical_aid_country_id: [null],
      medical_aid_scheme_id: [null],
      custom_scheme_name: [''],
      medical_aid_option_id: [null],
      custom_option_name: [''],
      medical_aid_plan_id: [null],
      custom_plan_name: [''],
      main_member_id: [''],
      dependant_code: [''],
      medical_aid_number: [''],
    });

    this.form = this.fb.group({
      first_name: [''],
      last_name: ['', Validators.required],
      person_title: [''],
      gender: ['', Validators.required],
      passport_number: ['', Validators.required],
      birthday: ['', Validators.required],
      phone: [null, [createPhoneNumberValidator(this.defaultCountryCode)]],
      email: [null, [Validators.email]],
      payment_method_id: ['', Validators.required],
      address: this.addressForm,
      medical_aid_details: this.medicalAidForm,
    });
  }

  private setupFormSubscriptions() {
    const paymentSub = this.form
      .get('payment_method_id')
      ?.valueChanges.subscribe((value) => {
        this.showMedicalAidFields = value == 2;
        this.toggleMedicalAidFields(this.showMedicalAidFields);
      });
    if (paymentSub) this.subscriptions.push(paymentSub);

    this.$countries = this.token$.pipe(
      filter((t): t is string => !!t),
      switchMap((token) =>
        this.countryService.getCountryList({
          headers: {
            Authorization: `Bearer ${token}`,
            'x-epione-source': this.appSource,
          },
        })
      ),
      map((res: CountryModel[]) => [
        { id: 0, code: '', name: 'Please Select', disabled: true },
        ...res,
      ])
    );

    const countrySub = this.medicalAidForm
      .get('medical_aid_country_id')
      ?.valueChanges.pipe(
        filter((countryId) => !!countryId),
        tap(() => {
          this.medicalAidForm.get('medical_aid_scheme_id')?.disable();
          this.medicalAidForm.get('medical_aid_plan_id')?.disable();
          this.medicalAidForm.get('medical_aid_option_id')?.disable();
        }),
        switchMap((countryId) =>
          this.medicalService
            .getSchemesByCountryId(countryId, {
              headers: {
                Authorization: `Bearer ${this.token$.value}`,
                'x-epione-source': this.appSource,
              },
            })
            .pipe(
              tap((schemes) => {
                this.schemes = schemes;
                this.updateMedicalAidValidators();
              }),
              finalize(() =>
                this.medicalAidForm.get('medical_aid_scheme_id')?.enable()
              ),
              catchError(() => {
                this.schemes = [];
                return of([]);
              })
            )
        )
      )
      .subscribe();
    if (countrySub) this.subscriptions.push(countrySub);

    const schemeSub = this.medicalAidForm
      .get('medical_aid_scheme_id')
      ?.valueChanges.pipe(
        filter((schemeId) => !!schemeId),
        tap(() => {
          this.medicalAidForm.get('medical_aid_plan_id')?.disable();
          this.medicalAidForm.get('medical_aid_option_id')?.disable();
        }),
        switchMap((schemeId) =>
          this.medicalService
            .getPlansBySchemeId(schemeId, {
              headers: {
                Authorization: `Bearer ${this.token$.value}`,
                'x-epione-source': this.appSource,
              },
            })
            .pipe(
              tap((plans) => (this.plans = plans)),
              finalize(() =>
                this.medicalAidForm.get('medical_aid_plan_id')?.enable()
              )
            )
        )
      )
      .subscribe();
    if (schemeSub) this.subscriptions.push(schemeSub);

    const planSub = this.medicalAidForm
      .get('medical_aid_plan_id')
      ?.valueChanges.pipe(
        filter((planId) => !!planId),
        tap(() => {
          this.medicalAidForm.get('medical_aid_option_id')?.disable();
        }),
        switchMap((planId) =>
          this.medicalService
            .getOptionsByPlanId(planId, {
              headers: {
                Authorization: `Bearer ${this.token$.value}`,
                'x-epione-source': this.appSource,
              },
            })
            .pipe(
              tap((options) => (this.options = options)),
              finalize(() =>
                this.medicalAidForm.get('medical_aid_option_id')?.enable()
              )
            )
        )
      )
      .subscribe();
    if (planSub) this.subscriptions.push(planSub);
  }

  private redeemToken(token: string) {
    this.tokenClaims = JSON.parse(atob(token.split('.')[1]));
    const sub = this.onboardingServiceService
      .redeemToken({
        headers: {
          Authorization: `Bearer ${token}`,
          'x-epione-source': this.appSource,
        },
      })
      .pipe(
        tap((res) => {
          this.token$.next(res.token);
          this.patchFormData(res.patient);
          this.practiceLogo = res.branding?.logo;
          this.toggleMedicalAidFields(this.showMedicalAidFields);
        }),
        switchMap((res) => {
          if (this.showMedicalAidFields && res?.patient?.medicalAidDetails) {
            return this.loadMedicalAidData(
              res.patient.medicalAidDetails,
              res.token
            );
          } else {
            return of(null);
          }
        }),
        catchError((err) => {
          console.error(err);
          this.error = 'Failed to load form.';
          return of(null);
        }),
        finalize(() => (this.loading = false))
      )
      .subscribe();
    this.subscriptions.push(sub);
  }

  private patchFormData(data: any) {
    this.form.patchValue({
      first_name: data.first_name,
      last_name: data.last_name,
      person_title: data.person_title,
      gender: data.gender,
      passport_number: data.passport_number,
      birthday: data.birthday,
      email: data.user?.email || null,
      payment_method_id: data.payment_method_id,
    });

    if (data.phones && Array.isArray(data.phones)) {
      const phoneObj = data.phones.find((p: any) => p.type_id === 2);
      if (phoneObj) {
        this.form.patchValue({ phone: phoneObj.phone || null });
      }
    }

    if (data.addresses && Array.isArray(data.addresses)) {
      const addressObj = data.addresses.find((a: any) => a.type_id === 1);
      if (addressObj) {
        this.addressForm.patchValue(addressObj);
      }
    }

    if (data.medicalAidDetails) {
      this.medicalAidForm.patchValue(data.medicalAidDetails);
      this.showMedicalAidFields = data.payment_method_id === 2;
    }
  }

  private loadMedicalAidData(details: any, token: string): Observable<any> {
    const {
      medical_aid_country_id,
      medical_aid_scheme_id,
      medical_aid_plan_id,
    } = details;
    if (!medical_aid_country_id) return of(null);

    return this.medicalService
      .getSchemesByCountryId(medical_aid_country_id, {
        headers: {
          Authorization: `Bearer ${token}`,
          'x-epione-source': this.appSource,
        },
      })
      .pipe(
        tap((schemes) => {
          this.schemes = schemes;
          this.updateMedicalAidValidators();
        }),
        switchMap(() => {
          if (!medical_aid_scheme_id) return of(null);
          return this.medicalService.getPlansBySchemeId(medical_aid_scheme_id, {
            headers: {
              Authorization: `Bearer ${token}`,
              'x-epione-source': this.appSource,
            },
          });
        }),
        tap((plans) => {
          if (plans) this.plans = plans;
        }),
        switchMap(() => {
          if (!medical_aid_plan_id) return of(null);
          return this.medicalService.getOptionsByPlanId(medical_aid_plan_id, {
            headers: {
              Authorization: `Bearer ${token}`,
              'x-epione-source': this.appSource,
            },
          });
        }),
        tap((options) => {
          if (options) this.options = options;
        }),
        finalize(() => {
          this.medicalAidForm.updateValueAndValidity();
        })
      );
  }

  private toggleMedicalAidFields(enable: boolean) {
    const controls = this.medicalAidForm.controls;
    Object.keys(controls).forEach((key) => {
      const control = controls[key];
      if (enable) {
        control.enable();
      } else {
        control.disable();
        control.clearValidators();
      }
    });
    this.updateMedicalAidValidators();

    this.medicalAidForm.updateValueAndValidity();
  }

  private updateMedicalAidValidators() {
    const schemeControl = this.medicalAidForm.get('medical_aid_scheme_id');
    const customSchemeControl = this.medicalAidForm.get('custom_scheme_name');
    const planControl = this.medicalAidForm.get('medical_aid_plan_id');
    const customPlanControl = this.medicalAidForm.get('custom_plan_name');
    const optionControl = this.medicalAidForm.get('medical_aid_option_id');
    const customOptionControl = this.medicalAidForm.get('custom_option_name');

    if (this.showMedicalAidFields) {
      if (this.schemes?.length) {
        schemeControl?.setValidators([Validators.required]);
        schemeControl?.enable({ emitEvent: false });

        planControl?.setValidators([Validators.required]);
        planControl?.enable({ emitEvent: false });

        optionControl?.clearValidators();
        optionControl?.enable({ emitEvent: false });

        customSchemeControl?.clearValidators();
        customSchemeControl?.disable({ emitEvent: false });

        customPlanControl?.clearValidators();
        customPlanControl?.disable({ emitEvent: false });

        customOptionControl?.clearValidators();
        customOptionControl?.disable({ emitEvent: false });
      } else {
        schemeControl?.clearValidators();
        schemeControl?.disable({ emitEvent: false });

        planControl?.clearValidators();
        planControl?.disable({ emitEvent: false });

        optionControl?.clearValidators();
        optionControl?.disable({ emitEvent: false });

        customSchemeControl?.setValidators([Validators.required]);
        customSchemeControl?.enable({ emitEvent: false });

        customPlanControl?.setValidators([Validators.required]);
        customPlanControl?.enable({ emitEvent: false });

        customOptionControl?.clearValidators();
        customOptionControl?.enable({ emitEvent: false });
      }

      this.medicalAidForm
        .get('main_member_id')
        ?.setValidators([Validators.required]);
      this.medicalAidForm
        .get('dependant_code')
        ?.setValidators([Validators.required]);
      this.medicalAidForm
        .get('medical_aid_number')
        ?.setValidators([Validators.required]);
      this.medicalAidForm
        .get('medical_aid_country_id')
        ?.setValidators([Validators.required]);
    } else {
      Object.keys(this.medicalAidForm.controls).forEach((key) => {
        const control = this.medicalAidForm.get(key);
        control?.clearValidators();
        control?.disable({ emitEvent: false });
      });
    }

    this.medicalAidForm.updateValueAndValidity({ emitEvent: false });
  }

  private updateValidationErrors() {
    const excludedKeys = ['address', 'medical_aid_details'];

    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (excludedKeys.includes(key) || control?.disabled) {
        delete this.validationErrors[key];
        return;
      }

      if (control?.invalid) {
        this.validationErrors[key] =
          this.validationErrors[key] || `Invalid value for ${key}`;
      } else {
        delete this.validationErrors[key];
      }
    });

    const addressControls = this.addressForm.controls;
    Object.keys(addressControls).forEach((key) => {
      if (addressControls[key].invalid) {
        this.validationErrors[key] =
          this.validationErrors[key] || `Invalid value for address ${key}`;
      }
    });

    const medicalAidControls = this.medicalAidForm.controls;
    Object.keys(medicalAidControls).forEach((key) => {
      if (
        this.schemes?.length &&
        (key === 'custom_scheme_name' ||
          key === 'custom_plan_name' ||
          key === 'custom_option_name')
      ) {
        return;
      }

      if (medicalAidControls[key].invalid) {
        this.validationErrors[key] =
          this.validationErrors[key] || `Invalid value for medical aid ${key}`;
      } else {
        delete this.validationErrors[key];
      }
    });
  }

  private initAutocomplete(element: ElementRef<HTMLInputElement>) {
    const autocomplete = new google.maps.places.Autocomplete(
      element.nativeElement,
      { types: ['address'] }
    );

    autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place: google.maps.places.PlaceResult = autocomplete.getPlace();
        this.handleAddressChange(place);
      });
    });
  }

  private handleAddressChange(place: google.maps.places.PlaceResult) {
    if (!place.address_components) {
      return;
    }

    const addressFields = {
      unit_number: '',
      complex: '',
      street: '',
      post_code: '',
      city: '',
      municipality: '',
      province: '',
      country_id: '',
    };

    let streetNumber = '';
    let route = '';
    let resolvedCountryName = '';

    for (const component of place.address_components) {
      const componentType = component.types[0];
      switch (componentType) {
        case 'street_number':
          streetNumber = component.long_name;
          break;
        case 'route':
          route = component.long_name;
          break;
        case 'postal_code':
          addressFields.post_code = component.long_name;
          break;
        case 'locality':
          addressFields.city = component.long_name;
          break;
        case 'administrative_area_level_1':
          addressFields.province = component.long_name;
          break;
        case 'administrative_area_level_2':
          addressFields.municipality = component.long_name;
          break;
        case 'country':
          resolvedCountryName = component.long_name;
          break;
        default:
          break;
      }
    }

    addressFields.street = (streetNumber + ' ' + route).trim();

    this.addressForm.patchValue(addressFields);

    if (resolvedCountryName) {
      this.patchCountryId(resolvedCountryName);
    }

    this.addressForm.get('street')?.markAsDirty();
    this.addressForm.get('post_code')?.markAsDirty();
    this.addressForm.get('city')?.markAsDirty();
    this.addressForm.get('municipality')?.markAsDirty();
    this.addressForm.get('province')?.markAsDirty();
    this.addressForm.get('country_id')?.markAsDirty();
  }

  private patchCountryId(countryName: string): void {
    const sub = this.$countries
      .pipe(
        filter((countries) => countries && countries.length > 1),
        map((countries) =>
          countries.find(
            (country) =>
              country.name.toLowerCase() === countryName.toLowerCase()
          )
        ),
        filter((country) => !!country),
        tap((country) => {
          this.addressForm.get('country_id')?.patchValue(country!.id);
          this.addressForm.get('country_id')?.markAsDirty();
        })
      )
      .subscribe();

    this.subscriptions.push(sub);
  }
}
